// src/features/tickets/components/TicketDetail.tsx
//
// Detail-Ansicht eines Tickets (Route /tickets/:ticketId).
// State und Workflow-Actions sitzen seit Schritt 8 im useTicketDetail-Hook;
// diese Komponente ist reine Praesentations-, Routing- und Toolbar-Schicht.

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Paperclip, Download, Loader, AlertTriangle, Clock, X, FileArchive, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import DOMPurify from 'dompurify';
import { supabase } from '../../../shared/services/supabase';
import { useTicketDetail } from '../hooks/useTicketDetail';
import { ticketService } from '../services/TicketService';
import { ticketAttachmentZipService, ZipProgress } from '../services/TicketAttachmentZipService';
import {
  TicketMessageWithAttachments,
  TicketAttachment,
  TicketStatus,
  TicketPriority,
  ScheduledTicketMessage,
  TicketKundenZuordnung
} from '../types';
import { TicketSidePanel } from './TicketSidePanel';
import { TicketReplyComposer } from './TicketReplyComposer';
import { TicketCustomerAssignModal } from './TicketCustomerAssignModal';
import { useTicketReplyTargets } from '../hooks/useTicketReplyTargets';
import { useTicketKundenKontext } from '../hooks/useTicketKundenKontext';
import { useTicketAnhangTexte } from '../hooks/useTicketAnhangTexte';
import { ticketAnhangTextService, TicketAnhangTextInfo } from '../services/TicketAnhangTextService';

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: 'new', label: 'Neu' },
  { value: 'open', label: 'Offen' },
  { value: 'pending', label: 'Wartet' },
  { value: 'resolved', label: 'Gelöst' },
  { value: 'closed', label: 'Geschlossen' }
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Niedrig' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'Hoch' }
];

// Entfernt eingebettete Inline-Bilder (src="cid:..." bzw. unquotiert src=cid:...)
// aus eingehendem Mail-HTML. cid:-Referenzen zeigen auf Inline-Parts der
// Original-MIME und sind ausserhalb dieses Containers nicht aufloesbar -> sie
// wuerden sonst als kaputte Bildkaesten erscheinen und das Signatur-Layout
// verschieben. Domino/Notes schreibt das src-Attribut ohne Anfuehrungszeichen,
// daher beide Formen abdecken.
const CID_IMG_REGEX = /<img\b[^>]*\bsrc\s*=\s*(?:["']cid:[^"']*["']|cid:[^\s>]+)[^>]*>/gi;

// Eingehende Mails werden auf einer weissen Flaeche dargestellt (wie in jedem
// Mailprogramm). Viele Outlook-Mails setzen fuer jeden Absatz einen weissen
// Hintergrund; auf der grauen Blase erschienen diese sonst als weisse Balken.
// Outlook liefert zudem im <head> die Regel, dass seine Absaetze keinen Abstand
// haben. Dieser <head> geht beim Bereinigen (DOMPurify) verloren, daher stellen
// wir den Abstand fuer die typischen Outlook-Absaetze hier wieder auf null.
// Gilt nur innerhalb der weissen Flaeche, also nur fuer eingehende Mails.
const MAIL_PAPER_CLASS = 'xtax-mail-paper';
const MAIL_PAPER_CSS =
  `.${MAIL_PAPER_CLASS} p.MsoNormal, .${MAIL_PAPER_CLASS} li.MsoNormal, ` +
  `.${MAIL_PAPER_CLASS} div.MsoNormal, .${MAIL_PAPER_CLASS} p.elementtoproof { margin: 0; }`;

export const TicketDetail: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();

  const {
    ticket,
    loading,
    staffList,
    actor,
    updating,
    reload,
    updateStatus,
    updatePriority,
    assignTicket,
    updateCustomerAssignment
  } = useTicketDetail(ticketId);

  const { replyAllCc } = useTicketReplyTargets(ticketId);

  // Zuordnungskontext (Absender, Regeln, Gruppe, Vorschlaege); laedt neu, wenn
  // sich Kunde oder Firmengruppe des Tickets aendern.
  const { kontext: kundenKontext } = useTicketKundenKontext(
    ticketId,
    ticket?.customer_id ?? null,
    ticket?.customer_group_id ?? null
  );

  // Status der Texterkennung je Anhang (aufklappbarer Text unter dem Anhang);
  // laedt mit jedem Neuladen des Tickets neu.
  const anhangTexte = useTicketAnhangTexte(ticket);

  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Zuordnungsfenster (Kunde / Firmengruppe / Pool)
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [preselectedCustomerId, setPreselectedCustomerId] = useState<string | null>(null);

  // Wrapper-Handler: zeigen alert() bei Fehlern (Hook gibt result zurueck)
  const handleStatusChange = async (newStatus: TicketStatus) => {
    const result = await updateStatus(newStatus);
    if (!result.success) {
      alert(`Status konnte nicht geändert werden: ${result.error}`);
    }
  };

  const handlePriorityChange = async (newPriority: TicketPriority) => {
    const result = await updatePriority(newPriority);
    if (!result.success) {
      alert(`Priorität konnte nicht geändert werden: ${result.error}`);
    }
  };

  const handleAssigneeChange = async (staffId: string) => {
    const newAssignee = staffId === '' ? null : staffId;
    const result = await assignTicket(newAssignee);
    if (!result.success) {
      alert(`Zuweisung konnte nicht geändert werden: ${result.error}`);
    }
  };

  const openCustomerModal = (preselectId?: string) => {
    setPreselectedCustomerId(preselectId ?? null);
    setShowCustomerModal(true);
  };

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setPreselectedCustomerId(null);
  };

  const handleCustomerSave = async (zuordnung: TicketKundenZuordnung) => {
    const result = await updateCustomerAssignment(zuordnung, kundenKontext);
    if (result.success) {
      closeCustomerModal();
    } else {
      alert(`Kundenzuordnung: ${result.error}`);
    }
  };

  const handleCancelScheduled = async (scheduledId: string) => {
    setCancellingId(scheduledId);
    try {
      const result = await ticketService.cancelScheduledMessage(scheduledId, actor);
      if (!result.success) {
        alert(`Geplante Sendung konnte nicht abgebrochen werden: ${result.error}`);
      }
      await reload();
    } finally {
      setCancellingId(null);
    }
  };

  // === Render ===

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
        <Loader size={24} style={{ marginBottom: '8px', animation: 'spin 1s linear infinite' }} />
        <div>Lade Ticket...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#6B7280' }}>
        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
          Ticket nicht gefunden
        </div>
        <button
          onClick={() => navigate('/tickets')}
          style={{
            marginTop: '12px',
            padding: '8px 16px',
            backgroundColor: '#2563EB',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Zurück zur Übersicht
        </button>
      </div>
    );
  }

  // Antwort-Empfaenger ("An"): Absender der juengsten eingehenden Kundennachricht.
  // Fallback auf die Hauptadresse des Kunden, wenn keine eingehende Nachricht mit
  // Absenderadresse vorliegt (z. B. bei intern gestarteten Tickets).
  const inboundSenders = ticket.messages
    .filter(m => m.author_type === 'customer' && !!m.author_email && m.author_email.trim() !== '')
    .slice()
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const latestInboundSender = inboundSenders.length > 0
    ? inboundSenders[inboundSenders.length - 1].author_email
    : null;
  const defaultToEmail = (latestInboundSender ?? ticket.customer?.email ?? '').trim();

  // Fehlgeschlagene geplante Sendungen werden nicht mehr als Banner gezeigt — der
  // Fehlschlag erscheint stattdessen als datierte System-Nachricht im Verlauf (und
  // hebt das Ticket dadurch in den Listen nach oben). pending/sending bleiben als
  // Banner sichtbar (inkl. Abbrechen).
  const visibleScheduledMessages = ticket.scheduled_messages.filter(s => s.status !== 'failed');

  // Anzeige der aktuellen Zuordnung in der Werkzeugleiste
  const customerLabel = ticket.customer
    ? ticket.customer.company
    : (ticket.customer_group ? `${ticket.customer_group.name} (Gesellschaft offen)` : 'Pool (kein Kunde)');

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Darstellungsregeln fuer eingehende Mails (siehe MAIL_PAPER_CSS) */}
      <style>{MAIL_PAPER_CSS}</style>

      {/* Zurueck-Link */}
      <button
        onClick={() => navigate('/tickets')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: '#6B7280',
          fontSize: '14px',
          cursor: 'pointer',
          padding: '4px 0',
          marginBottom: '12px'
        }}
      >
        <ArrowLeft size={16} />
        Zurück zur Übersicht
      </button>

      {/* Kopf: Ticket-Nr + Betreff */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontSize: '13px',
          fontFamily: 'monospace',
          color: '#6B7280',
          marginBottom: '4px'
        }}>
          {ticket.ticket_number}
        </div>
        <h1 style={{
          fontSize: '22px',
          fontWeight: '700',
          color: '#111827',
          margin: 0
        }}>
          {ticket.subject || '(kein Betreff)'}
        </h1>
      </div>

      {/* Workflow-Toolbar */}
      <div style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        padding: '14px 16px',
        backgroundColor: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <ToolbarSelect
          label="Status"
          value={ticket.status}
          options={STATUS_OPTIONS}
          disabled={updating}
          onChange={(v) => handleStatusChange(v as TicketStatus)}
        />
        <ToolbarSelect
          label="Priorität"
          value={ticket.priority}
          options={PRIORITY_OPTIONS}
          disabled={updating}
          onChange={(v) => handlePriorityChange(v as TicketPriority)}
        />
        <ToolbarSelect
          label="Zugewiesen"
          value={ticket.assigned_to_staff_id || ''}
          options={[
            { value: '', label: 'Nicht zugewiesen' },
            ...staffList.map(s => ({ value: s.id, label: s.fullName }))
          ]}
          disabled={updating}
          onChange={handleAssigneeChange}
        />
        <ToolbarButtonField
          label="Kunde"
          value={customerLabel}
          muted={!ticket.customer}
          disabled={updating}
          onClick={() => openCustomerModal()}
        />
        {updating && (
          <span style={{ color: '#6B7280', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px', paddingBottom: '8px' }}>
            <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
            Speichern...
          </span>
        )}
      </div>

      {/* 2-Spalten-Layout: Thread + SidePanel */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Thread */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Reply-/Notiz-Composer (oberhalb des Threads) */}
          <TicketReplyComposer
            ticketId={ticket.id}
            defaultToEmail={defaultToEmail}
            replyAllCcEmails={replyAllCc}
            actor={actor}
            onSent={reload}
          />

          {/* Geplante Sendungen (pending/sending) — fehlgeschlagene werden als
              System-Nachricht im Verlauf gezeigt, nicht mehr als Banner */}
          {visibleScheduledMessages.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
              {visibleScheduledMessages.map(scheduled => (
                <ScheduledBanner
                  key={scheduled.id}
                  scheduled={scheduled}
                  cancelling={cancellingId === scheduled.id}
                  onCancel={() => handleCancelScheduled(scheduled.id)}
                />
              ))}
            </div>
          )}

          {/* Nachrichten: neueste zuerst (Render-Reihenfolge umgekehrt, Daten unveraendert) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
            {ticket.messages.length === 0 ? (
              <div style={{ color: '#9CA3AF', fontSize: '14px', padding: '20px' }}>
                Noch keine Nachrichten in diesem Ticket.
              </div>
            ) : (
              ticket.messages.slice().reverse().map(message => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  ticketNumber={ticket.ticket_number}
                  anhangTexte={anhangTexte}
                />
              ))
            )}
          </div>
        </div>

        {/* SidePanel */}
        <TicketSidePanel
          ticket={ticket}
          staffList={staffList}
          kundenKontext={kundenKontext}
          onOpenCustomerAssign={openCustomerModal}
          onCustomerUpdated={reload}
        />
      </div>

      {/* Zuordnungsfenster Kunde / Firmengruppe / Pool */}
      <TicketCustomerAssignModal
        isOpen={showCustomerModal}
        currentCustomerId={ticket.customer_id}
        currentGroupId={ticket.customer_group_id ?? null}
        kontext={kundenKontext}
        preselectedCustomerId={preselectedCustomerId}
        saving={updating}
        onClose={closeCustomerModal}
        onSave={handleCustomerSave}
      />
    </div>
  );
};

// ============================================
// Geplante Sendung (Banner)
// ============================================

// pending  -> blauer Hinweis + Abbrechen-Button
// sending  -> blauer Hinweis "Wird gesendet ..." ohne Abbrechen (laeuft bereits)
// failed   -> roter Fehlerhinweis ohne Abbrechen
// Zeigt jeweils Empfaenger, CC (falls vorhanden), Zeitpunkt, Ersteller, ggf. die
// Außenidentität „Senden als" und den vollstaendigen Nachrichteninhalt — so wie
// die Mail tatsaechlich an den Kunden rausgeht (HTML via DOMPurify,
// cid:-Inline-Bilder gestrippt; Fallback Text).
const ScheduledBanner: React.FC<{
  scheduled: ScheduledTicketMessage;
  cancelling: boolean;
  onCancel: () => void;
}> = ({ scheduled, cancelling, onCancel }) => {
  const isFailed = scheduled.status === 'failed';
  const canCancel = scheduled.status === 'pending';

  const textColor = isFailed ? '#7F1D1D' : '#1E3A8A';
  const labelColor = isFailed ? '#B91C1C' : '#3B82F6';

  const header = isFailed
    ? 'Geplante Sendung fehlgeschlagen'
    : (scheduled.status === 'sending' ? 'Wird gesendet …' : 'Antwort geplant');

  const detailRows: { label: string; value: string }[] = [
    { label: 'An', value: scheduled.to_emails.join(', ') || '—' }
  ];
  if (scheduled.cc_emails.length > 0) {
    detailRows.push({ label: 'CC', value: scheduled.cc_emails.join(', ') });
  }
  detailRows.push({ label: 'Zeitpunkt', value: `${formatBerlinDateTime(scheduled.scheduled_at)} Uhr` });
  if (scheduled.created_by_name) {
    detailRows.push({ label: 'Von', value: scheduled.created_by_name });
  }
  // Außenidentität „Senden als": nur gesetzt, wenn abweichend vom Ersteller
  // gewählt — die Mail geht dann mit diesem Namen (From + Signatur) raus.
  if (scheduled.sent_as_name) {
    detailRows.push({ label: 'Senden als', value: scheduled.sent_as_name });
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '12px 14px',
      backgroundColor: isFailed ? '#FEF2F2' : '#EFF6FF',
      border: `1px solid ${isFailed ? '#FECACA' : '#BFDBFE'}`,
      borderRadius: '8px',
      flexWrap: 'wrap'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0, flex: 1 }}>
        {isFailed
          ? <AlertTriangle size={16} style={{ color: '#DC2626', marginTop: '2px', flexShrink: 0 }} />
          : <Clock size={16} style={{ color: '#2563EB', marginTop: '2px', flexShrink: 0 }} />}
        <div style={{ fontSize: '13px', color: textColor, minWidth: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{header}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '8px', rowGap: '2px' }}>
            {detailRows.map(row => (
              <React.Fragment key={row.label}>
                <span style={{ color: labelColor }}>{row.label}:</span>
                <span style={{ wordBreak: 'break-word' }}>{row.value}</span>
              </React.Fragment>
            ))}
          </div>
          {/* Vollstaendiger Nachrichteninhalt — identische Aufbereitung wie bei
              ausgehenden Staff-Nachrichten in der MessageBubble, damit ersichtlich
              ist, was exakt an den Kunden verschickt wird. Kein Zitat-Toggle
              (Staff-Nachricht). */}
          <div style={{ marginTop: '8px' }}>
            {scheduled.body_html ? (
              <MessageHtmlBody
                html={DOMPurify.sanitize(scheduled.body_html.replace(CID_IMG_REGEX, ''))}
                splitQuotes={false}
              />
            ) : (
              <div style={{
                fontSize: '14px',
                color: '#111827',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: '1.5'
              }}>
                {scheduled.body_text || '(kein Textinhalt)'}
              </div>
            )}
          </div>
          {isFailed && scheduled.last_error && (
            <div style={{ marginTop: '6px', color: '#991B1B', wordBreak: 'break-word' }}>
              {scheduled.last_error}
              {scheduled.attempts > 0 ? ` (${scheduled.attempts} Versuche)` : ''}
            </div>
          )}
        </div>
      </div>
      {canCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '5px 10px',
            backgroundColor: 'white',
            border: '1px solid #BFDBFE',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            color: '#1D4ED8',
            cursor: cancelling ? 'not-allowed' : 'pointer',
            opacity: cancelling ? 0.6 : 1
          }}
        >
          {cancelling
            ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <X size={13} />}
          Abbrechen
        </button>
      )}
    </div>
  );
};

// ============================================
// Toolbar-Select
// ============================================

interface ToolbarSelectProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (value: string) => void;
}

const ToolbarSelect: React.FC<ToolbarSelectProps> = ({ label, value, options, disabled, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <label style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280' }}>{label}</label>
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '7px 10px',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '14px',
        backgroundColor: disabled ? '#F3F4F6' : 'white',
        color: '#111827',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minWidth: '160px'
      }}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// ============================================
// Toolbar-Feld mit Button (oeffnet ein Fenster statt einer Auswahlliste)
// ============================================

interface ToolbarButtonFieldProps {
  label: string;
  value: string;
  muted?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

// Optisch an ToolbarSelect angelehnt, damit die Werkzeugleiste einheitlich wirkt.
const ToolbarButtonField: React.FC<ToolbarButtonFieldProps> = ({ label, value, muted, disabled, onClick }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <label style={{ fontSize: '12px', fontWeight: '500', color: '#6B7280' }}>{label}</label>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '7px 10px',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '14px',
        backgroundColor: disabled ? '#F3F4F6' : 'white',
        color: muted ? '#6B7280' : '#111827',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minWidth: '220px',
        maxWidth: '360px',
        textAlign: 'left'
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
      <ChevronDown size={14} color="#6B7280" style={{ flexShrink: 0 }} />
    </button>
  </div>
);

// ============================================
// Message-Bubble
// ============================================

const MessageBubble: React.FC<{
  message: TicketMessageWithAttachments;
  ticketNumber: string;
  anhangTexte: Record<string, TicketAnhangTextInfo>;
}> = ({ message, ticketNumber, anhangTexte }) => {
  // System-Nachricht: zentriert, schlicht
  if (message.author_type === 'system') {
    // Bounce-Alert (Stufe 4.7): auffaelliger Warnbanner statt dezentem Zentral-Text.
    if (message.message_type === 'bounce_alert') {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderLeft: '4px solid #DC2626',
          borderRadius: '8px',
          padding: '12px 14px'
        }}>
          <AlertTriangle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '500',
              color: '#7F1D1D',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: '1.5'
            }}>
              {message.body_text || 'Zustellung fehlgeschlagen'}
            </div>
            <div style={{ fontSize: '11px', color: '#B91C1C', marginTop: '6px' }}>
              {formatDateTime(message.created_at)}
            </div>
          </div>
        </div>
      );
    }

    // Sonstige System-Nachricht: zentriert, schlicht.
    return (
      <div style={{ textAlign: 'center', padding: '4px 0' }}>
        <span style={{
          display: 'inline-block',
          fontSize: '12px',
          color: '#9CA3AF',
          backgroundColor: '#F3F4F6',
          borderRadius: '12px',
          padding: '4px 12px'
        }}>
          {message.body_text || 'Systemereignis'} · {formatDateTime(message.created_at)}
        </span>
      </div>
    );
  }

  const isInternal = message.is_internal_note;
  const isStaff = message.author_type === 'staff';

  // Farb-/Ausrichtungs-Logik:
  //   interne Notiz -> gelb, linksbuendig, mit Label
  //   Staff-Antwort -> blau, rechtsbuendig
  //   Kunde         -> grau, linksbuendig
  const alignment = isStaff && !isInternal ? 'flex-end' : 'flex-start';
  const bubbleBg = isInternal ? '#FEF9C3' : (isStaff ? '#EFF6FF' : '#F9FAFB');
  const bubbleBorder = isInternal ? '#FDE68A' : (isStaff ? '#BFDBFE' : '#E5E7EB');

  const authorName = message.author_staff
    ? message.author_staff.full_name
    : (message.author_name || message.author_email || 'Unbekannt');

  // Absenderadresse eingehender (Kunden-)Nachrichten sichtbar machen. Bei
  // Staff-Antworten zeigt stattdessen die "An:"-Zeile die Empfaenger.
  const senderEmail = !isStaff && message.author_email ? message.author_email.trim() : '';
  const showSenderEmail = senderEmail !== '' && senderEmail !== authorName;

  return (
    <div style={{ display: 'flex', justifyContent: alignment }}>
      <div style={{
        maxWidth: '78%',
        backgroundColor: bubbleBg,
        border: `1px solid ${bubbleBorder}`,
        borderRadius: '10px',
        padding: '12px 14px'
      }}>
        {/* Header: Autor + Zeit + ggf. Notiz-Label */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '6px'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
            {authorName}
            {showSenderEmail && (
              <span style={{ marginLeft: '6px', fontSize: '12px', fontWeight: 400, color: '#6B7280' }}>
                · {senderEmail}
              </span>
            )}
            {/* Außenidentität „Senden als": die Mail ging mit diesem Namen (From,
                Signatur) an den Kunden raus; intern bleibt der echte Autor sichtbar. */}
            {message.sent_as_name && message.sent_as_name !== authorName && (
              <span style={{ marginLeft: '6px', fontSize: '12px', fontWeight: 400, color: '#6B7280' }}>
                · gesendet als {message.sent_as_name}
              </span>
            )}
            {isInternal && (
              <span style={{
                marginLeft: '8px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#92400E',
                backgroundColor: '#FDE68A',
                padding: '1px 6px',
                borderRadius: '4px'
              }}>
                Interne Notiz
              </span>
            )}
          </span>
          <span style={{ fontSize: '12px', color: '#9CA3AF', flexShrink: 0 }}>
            {formatDateTime(message.created_at)}
          </span>
        </div>

        {/* Empfaenger der Mail — ausgehend aus outbound_mail_log, eingehend aus ticket_inbound_log */}
        {message.to_emails && message.to_emails.length > 0 && (
          <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
            An: {message.to_emails.join(', ')}
          </div>
        )}

        {/* CC-Empfaenger der Mail (ein- und ausgehend), sofern vorhanden */}
        {message.cc_emails && message.cc_emails.length > 0 && (
          <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
            CC: {message.cc_emails.join(', ')}
          </div>
        )}

        {/* Body: HTML-Nachrichten (Kunde via Mail, Staff via Rich-Text-Composer)
            werden DOMPurify-sanitisiert gerendert; reine Textnachrichten als
            Plain-Text mit pre-wrap. Altdaten ohne body_html bleiben Text. */}
        {message.body_html ? (
          <MessageHtmlBody
            html={DOMPurify.sanitize(message.body_html.replace(CID_IMG_REGEX, ''))}
            splitQuotes={!isStaff}
            paper={!isStaff}
          />
        ) : (
          <div style={{
            fontSize: '14px',
            color: '#111827',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: '1.5'
          }}>
            {message.body_text || '(kein Textinhalt)'}
          </div>
        )}

        {/* Anhaenge */}
        {message.attachments.length > 0 && (
          <div style={{
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: `1px solid ${bubbleBorder}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
          }}>
            <AttachmentZipButton
              ticketNumber={ticketNumber}
              messageId={message.id}
              messageCreatedAt={message.created_at}
              attachments={message.attachments}
            />
            {message.attachments.map(att => (
              <div key={att.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <AttachmentChip storagePath={att.storage_path} filename={att.filename} fileSize={att.file_size} />
                <AttachmentTextPanel attachmentId={att.id} info={anhangTexte[att.id] ?? null} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Message-HTML-Body mit Zitat-Toggle (Stufe 3)
// ============================================

// Rendert sanitisiertes Kunden-HTML und blendet einen erkannten zitierten
// Verlauf hinter einen Toggle (standardmaessig eingeklappt). Wird keine
// Zitat-Grenze gefunden, ist die Anzeige identisch zur frueheren Direkt-
// Ausgabe (es wird dann das unveraenderte HTML gerendert).
//
// Umbruch-Verhalten: overflow-wrap 'break-word' (nicht 'anywhere') und
// word-break 'normal', damit der Browser bei tabellenbasierten Fremd-Signaturen
// (z. B. aus Outlook/Word) die Spalten NICHT auf Einzelzeichen-Breite quetscht
// und jedes Zeichen auf eine eigene Zeile bricht. overflow-x 'auto' faengt zu
// breite Layouts ab: sie scrollen horizontal statt zu zerfallen.
//
// paper=true (eingehende Mails): weisse Flaeche mit Rahmen und Innenabstand,
// dazu die Outlook-Absatzregeln aus MAIL_PAPER_CSS.
const MessageHtmlBody: React.FC<{ html: string; splitQuotes?: boolean; paper?: boolean }> = ({ html, splitQuotes = true, paper = false }) => {
  const [showQuoted, setShowQuoted] = useState(false);
  const { visible, quoted } = splitQuotes ? splitQuotedHtml(html) : { visible: html, quoted: null };

  return (
    <div
      className={paper ? MAIL_PAPER_CLASS : undefined}
      style={{
        fontSize: '14px',
        color: '#111827',
        wordBreak: 'normal',
        overflowWrap: 'break-word',
        overflowX: 'auto',
        lineHeight: '1.5',
        ...(paper ? {
          backgroundColor: 'white',
          border: '1px solid #EEF0F3',
          borderRadius: '6px',
          padding: '10px 12px'
        } : {})
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: visible }} />
      {quoted && (
        <>
          <button
            type="button"
            onClick={() => setShowQuoted(v => !v)}
            style={{
              display: 'inline-block',
              marginTop: '8px',
              padding: '2px 8px',
              fontSize: '12px',
              color: '#6B7280',
              backgroundColor: '#F3F4F6',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showQuoted ? 'Zitierten Verlauf ausblenden' : 'Zitierten Verlauf anzeigen'}
          </button>
          {showQuoted && (
            <div
              style={{
                marginTop: '8px',
                paddingLeft: '12px',
                borderLeft: '2px solid #E5E7EB',
                color: '#6B7280'
              }}
              dangerouslySetInnerHTML={{ __html: quoted }}
            />
          )}
        </>
      )}
    </div>
  );
};

// ============================================
// Attachment-Chip mit Download
// ============================================

const AttachmentChip: React.FC<{ storagePath: string; filename: string; fileSize: number }> = ({ storagePath, filename, fileSize }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // download: filename setzt die Content-Disposition auf attachment mit dem
      // Anzeigenamen (inkl. Umlauten) — unabhaengig vom ASCII-Storage-Key.
      const { data, error } = await supabase.storage
        .from('ticket-attachments')
        .createSignedUrl(storagePath, 300, { download: filename });

      if (error || !data?.signedUrl) {
        console.error('[AttachmentChip] Signed-URL-Error:', error);
        alert('Anhang konnte nicht geladen werden.');
        return;
      }
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('[AttachmentChip] Unexpected error:', err);
      alert('Anhang konnte nicht geladen werden.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        backgroundColor: 'white',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        cursor: downloading ? 'wait' : 'pointer',
        fontSize: '13px',
        color: '#374151',
        width: 'fit-content',
        maxWidth: '100%'
      }}
    >
      <Paperclip size={14} color="#6B7280" style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {filename}
      </span>
      <span style={{ color: '#9CA3AF', flexShrink: 0 }}>({formatFileSize(fileSize)})</span>
      {downloading ? <Loader size={13} style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }} /> : <Download size={13} color="#6B7280" style={{ flexShrink: 0 }} />}
    </button>
  );
};

// ============================================
// Erkannter Text eines Anhangs (aufklappbar)
// ============================================

// Zeigt unter dem Anhang den Stand der Texterkennung (ticket_anhang_texte).
// Liegt Text vor, ist er standardmaessig zugeklappt und wird erst beim
// Aufklappen geladen, damit das Ticket schnell bleibt. Dateitypen ohne
// Texterkennung (z. B. Archive) zeigen nichts an. Der Text ist auch Teil der
// Ticketsuche; das erledigt die Datenbank.
const TEXT_STATUS_HINWEIS: Record<string, string> = {
  offen: 'Texterkennung ausstehend',
  laufend: 'Text wird erkannt …',
  kein_text: 'Kein Text erkennbar',
  zu_gross: 'Zu groß für die Texterkennung',
  fehlgeschlagen: 'Texterkennung fehlgeschlagen'
};

const AttachmentTextPanel: React.FC<{ attachmentId: string; info: TicketAnhangTextInfo | null }> = ({ attachmentId, info }) => {
  const [offen, setOffen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  if (!info || info.status === 'nicht_unterstuetzt') return null;

  if (info.status !== 'fertig') {
    const hinweis = TEXT_STATUS_HINWEIS[info.status];
    if (!hinweis) return null;
    return (
      <span
        title={info.letzter_fehler ?? undefined}
        style={{ fontSize: '12px', color: '#9CA3AF', paddingLeft: '2px' }}
      >
        {hinweis}
      </span>
    );
  }

  const handleToggle = async () => {
    if (offen) {
      setOffen(false);
      return;
    }
    setOffen(true);
    if (text !== null || laedt) return;
    setLaedt(true);
    setFehler(null);
    try {
      const result = await ticketAnhangTextService.ladeText(attachmentId);
      if (result.success && typeof result.text === 'string') {
        setText(result.text);
      } else {
        setFehler(result.error ?? 'Der Text konnte nicht geladen werden.');
      }
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <button
        type="button"
        onClick={handleToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '0 2px',
          background: 'none',
          border: 'none',
          fontSize: '12px',
          color: '#6B7280',
          cursor: 'pointer',
          width: 'fit-content'
        }}
      >
        {offen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <FileText size={13} />
        Erkannter Text
      </button>
      {offen && (
        <div style={{
          maxHeight: '320px',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '12px',
          lineHeight: '1.5',
          color: '#374151',
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          padding: '8px 10px'
        }}>
          {laedt && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#6B7280' }}>
              <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
              Lade Text …
            </span>
          )}
          {!laedt && fehler && (
            <span style={{ color: '#B91C1C' }}>{fehler}</span>
          )}
          {!laedt && !fehler && text !== null && (
            <>
              {info.gekuerzt && (
                <div style={{ color: '#92400E', marginBottom: '6px' }}>
                  Der Text ist sehr lang und wurde nur teilweise gespeichert.
                </div>
              )}
              {text}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// Sammel-Download aller Anhänge einer Nachricht als ZIP
// ============================================

// Erscheint erst ab zwei unterschiedlichen Dateien. Mehrere Anhangszeilen auf
// demselben storage_path zählen als eine Datei (identisch zur Deduplizierung
// im TicketAttachmentZipService). Alles-oder-nichts: bei einem Fehler entsteht
// kein ZIP, der Fehlertext nennt die betroffene Datei.
const AttachmentZipButton: React.FC<{
  ticketNumber: string;
  messageId: string;
  messageCreatedAt: string;
  attachments: TicketAttachment[];
}> = ({ ticketNumber, messageId, messageCreatedAt, attachments }) => {
  const [progress, setProgress] = useState<ZipProgress | null>(null);

  const fileCount = new Set(attachments.map(att => att.storage_path)).size;
  if (fileCount < 2) return null;

  const running = progress !== null;

  const handleClick = async () => {
    if (running) return;
    setProgress({ phase: 'download', done: 0, total: fileCount });
    try {
      const result = await ticketAttachmentZipService.downloadMessageAttachments({
        ticketNumber,
        messageId,
        messageCreatedAt,
        attachments,
        onProgress: setProgress
      });
      if (!result.success) {
        alert(result.error ?? 'Das ZIP konnte nicht erstellt werden.');
      }
    } finally {
      setProgress(null);
    }
  };

  const label = !progress
    ? `Alle ${fileCount} Anhänge herunterladen (ZIP)`
    : (progress.phase === 'download'
      ? `Lade ${progress.done}/${progress.total} …`
      : 'ZIP wird erstellt …');

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={running}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        backgroundColor: 'white',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        cursor: running ? 'wait' : 'pointer',
        fontSize: '13px',
        fontWeight: 500,
        color: '#374151',
        width: 'fit-content',
        maxWidth: '100%'
      }}
    >
      {running
        ? <Loader size={14} style={{ flexShrink: 0, animation: 'spin 1s linear infinite' }} />
        : <FileArchive size={14} color="#6B7280" style={{ flexShrink: 0 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  );
};

// ============================================
// Helpers
// ============================================

function formatDateTime(isoTimestamp: string | null): string {
  if (!isoTimestamp) return '—';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy}, ${hh}:${min}`;
}

// Formatiert einen UTC-Zeitstempel fest in Europe/Berlin (fuer geplante Sendungen,
// die intern als UTC gespeichert, aber als Berliner Zeit terminiert werden).
function formatBerlinDateTime(isoTimestamp: string | null): string {
  if (!isoTimestamp) return '—';
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${m.day}.${m.month}.${m.year}, ${m.hour}:${m.minute}`;
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

// Splittet sanitisiertes Mail-HTML in sichtbare Antwort und zitierten Verlauf.
// Erkennt die Zitat-Grenze in Prioritaetsreihenfolge:
//   1. Gmail: Element mit Klasse "gmail_quote"
//   2. Outlook (OWA): Element mit id "divRplyFwdMsg"
//   3. Generisch: oberstes <blockquote>
// Geteilt wird auf Ebene der direkten body-Kinder: das Kind, das die Grenze
// enthaelt, und alle danach gelten als zitierter Verlauf; alles davor als
// sichtbare Antwort. Gibt quoted=null zurueck, wenn keine Grenze gefunden wird,
// die Grenze bereits das erste Kind ist (kein sichtbarer Teil davor) oder der
// sichtbare Teil nur Leerraum ist — dann bleibt die Anzeige unveraendert.
function splitQuotedHtml(html: string): { visible: string; quoted: string | null } {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return { visible: html, quoted: null };
  }

  const body = doc.body;
  if (!body) {
    return { visible: html, quoted: null };
  }

  const boundary =
    body.querySelector('.gmail_quote') ||
    body.querySelector('#divRplyFwdMsg') ||
    body.querySelector('blockquote');
  if (!boundary) {
    return { visible: html, quoted: null };
  }

  const topChildren = Array.from(body.childNodes);
  let boundaryIndex = -1;
  for (let i = 0; i < topChildren.length; i++) {
    const child = topChildren[i];
    if (child === boundary || child.contains(boundary)) {
      boundaryIndex = i;
      break;
    }
  }
  if (boundaryIndex <= 0) {
    return { visible: html, quoted: null };
  }

  const visibleFrag = doc.createElement('div');
  const quotedFrag = doc.createElement('div');
  topChildren.forEach((node, i) => {
    (i < boundaryIndex ? visibleFrag : quotedFrag).appendChild(node.cloneNode(true));
  });

  const visibleText = visibleFrag.textContent?.trim() ?? '';
  const quotedHtml = quotedFrag.innerHTML.trim();
  if (visibleText.length === 0 || quotedHtml.length === 0) {
    return { visible: html, quoted: null };
  }

  return { visible: visibleFrag.innerHTML.trim(), quoted: quotedHtml };
}
